import { useRef, useState, useEffect, useMemo } from "react";
import { Upload, RotateCcw, Sparkles, ZoomIn, ZoomOut, Hand, MousePointer2, Share2, Download, Check, X as XIcon, Crosshair, Pipette } from "lucide-react";
import html2canvas from "html2canvas";
import { useT } from "../lib/i18n";

// Language-aware label picker: returns EN label when lang is EN, else FR.
const pickLabel = (obj, lang) => (lang === "en" && obj.label_en ? obj.label_en : obj.label);

/** Three perfect wall scenes — mostly wall (85%) with subtle decor
 *  (sofa, chair, plant) at the bottom. No people, no clutter, guaranteed
 *  clean wall surface for hanging artwork at any size or angle.
 */
const ROOMS = [
  {
    id: "blanc",
    kind: "styled",
    label: "Salon clair",
    style: {
      background:
        "linear-gradient(180deg, #fafaf6 0%, #f4f1e9 84%, #e3d9c4 84.5%, #cab48f 100%)",
    },
    decor: "scandi",
  },
  {
    id: "creme",
    kind: "styled",
    label: "Bureau chaleureux",
    label_en: "Warm office",
    style: {
      background:
        "linear-gradient(180deg, #efeadd 0%, #e9e2cf 84%, #a98049 84.5%, #6b4a26 100%)",
    },
    decor: "warm",
  },
  {
    id: "charbon",
    kind: "styled",
    label: "Atelier moderne",
    label_en: "Modern studio",
    style: {
      background:
        "linear-gradient(180deg, #2a2a2a 0%, #1f1f1f 84%, #161616 84.5%, #0a0a0a 100%)",
    },
    decor: "dark",
  },
];

/** Stylised SVG decor — sofas, chairs, plants, lamp. Sits at the bottom of
 *  the scene so the wall above stays completely free for artwork.
 */
const SceneDecor = ({ kind }) => {
  if (kind === "scandi") {
    return (
      <svg
        viewBox="0 0 1000 220"
        preserveAspectRatio="xMidYMax meet"
        className="absolute left-0 right-0 bottom-0 w-full h-[36%] pointer-events-none"
      >
        <defs>
          <linearGradient id="boucleFabric" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#dccdb1" />
            <stop offset="55%" stopColor="#c6b694" />
            <stop offset="100%" stopColor="#a89878" />
          </linearGradient>
          <linearGradient id="oakLeg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#7a5a36" />
            <stop offset="100%" stopColor="#3e2a14" />
          </linearGradient>
          <radialGradient id="vaseSheen" cx="40%" cy="35%" r="60%">
            <stop offset="0%" stopColor="#d9b78a" />
            <stop offset="100%" stopColor="#7a5832" />
          </radialGradient>
        </defs>

        {/* Bouclé armchair (left) — rounded scandinavian style */}
        <g transform="translate(110 30)">
          {/* back cushion */}
          <path d="M 0 70 Q 0 20 90 18 Q 180 20 180 70 L 180 120 L 0 120 Z" fill="url(#boucleFabric)" />
          {/* seat cushion */}
          <path d="M -6 110 Q -6 100 8 100 L 172 100 Q 186 100 186 110 L 186 150 Q 186 162 172 162 L 8 162 Q -6 162 -6 150 Z" fill="#cbb993" />
          {/* armrests */}
          <ellipse cx="6" cy="110" rx="14" ry="32" fill="#c4b289" />
          <ellipse cx="174" cy="110" rx="14" ry="32" fill="#c4b289" />
          {/* legs */}
          <rect x="22" y="158" width="8" height="22" fill="url(#oakLeg)" rx="1" />
          <rect x="150" y="158" width="8" height="22" fill="url(#oakLeg)" rx="1" />
          {/* highlight */}
          <ellipse cx="90" cy="60" rx="60" ry="10" fill="#ffffff" opacity="0.15" />
        </g>

        {/* Pampas in tall vase (right) */}
        <g transform="translate(820 0)">
          {/* vase */}
          <path d="M 22 110 L 76 110 L 70 188 L 28 188 Z" fill="url(#vaseSheen)" />
          <ellipse cx="49" cy="110" rx="27" ry="4" fill="#3a2812" />
          <ellipse cx="49" cy="110" rx="22" ry="2.5" fill="#1a1108" />
          {/* fluffy plumes */}
          {[
            { x: 30, dx: -16, c: "#f0deb1" },
            { x: 42, dx: -8,  c: "#e9d4a3" },
            { x: 54, dx: 6,   c: "#efdcae" },
            { x: 66, dx: 18,  c: "#e8d199" },
          ].map((p, i) => (
            <g key={i}>
              <path
                d={`M ${p.x} 110 Q ${p.x + p.dx} 80 ${p.x + p.dx * 1.4} 24`}
                stroke="#b5996a"
                strokeWidth="2"
                fill="none"
              />
              {Array.from({ length: 14 }).map((_, j) => (
                <ellipse
                  key={j}
                  cx={p.x + (p.dx * (j + 1)) / 14}
                  cy={110 - (86 * (j + 1)) / 14}
                  rx={2.4 - j * 0.05}
                  ry={6 - j * 0.18}
                  fill={p.c}
                  opacity={0.92}
                />
              ))}
            </g>
          ))}
        </g>

        {/* Floor shadows */}
        <ellipse cx="200" cy="195" rx="115" ry="6" fill="#000" opacity="0.18" />
        <ellipse cx="868" cy="195" rx="48"  ry="5" fill="#000" opacity="0.2" />
      </svg>
    );
  }

  if (kind === "warm") {
    return (
      <svg
        viewBox="0 0 1000 220"
        preserveAspectRatio="xMidYMax meet"
        className="absolute left-0 right-0 bottom-0 w-full h-[38%] pointer-events-none"
      >
        <defs>
          <linearGradient id="leatherTop" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#9a5d34" />
            <stop offset="55%" stopColor="#7a4222" />
            <stop offset="100%" stopColor="#48270f" />
          </linearGradient>
          <linearGradient id="leatherSeat" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#874627" />
            <stop offset="100%" stopColor="#52281a" />
          </linearGradient>
          <linearGradient id="brass" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#e8c987" />
            <stop offset="100%" stopColor="#8a6638" />
          </linearGradient>
        </defs>

        {/* Tan leather lounge chair */}
        <g transform="translate(150 14)">
          {/* backrest */}
          <path d="M 0 72 Q 0 14 100 12 Q 200 14 200 72 L 200 110 L 0 110 Z" fill="url(#leatherTop)" />
          {/* button-tufting hint */}
          {[40, 80, 120, 160].map((x) => (
            <g key={x}>
              <circle cx={x} cy="40" r="2" fill="#3a1f0a" />
              <circle cx={x} cy="68" r="2" fill="#3a1f0a" />
            </g>
          ))}
          {/* seat */}
          <path d="M -8 102 L 208 102 L 200 168 L 0 168 Z" fill="url(#leatherSeat)" />
          {/* armrests */}
          <ellipse cx="2"   cy="100" rx="18" ry="42" fill="#6b3a1f" />
          <ellipse cx="198" cy="100" rx="18" ry="42" fill="#6b3a1f" />
          {/* legs (brass) */}
          <rect x="18"  y="166" width="6" height="22" fill="url(#brass)" />
          <rect x="176" y="166" width="6" height="22" fill="url(#brass)" />
          {/* highlight */}
          <ellipse cx="100" cy="50" rx="70" ry="8" fill="#ffffff" opacity="0.12" />
        </g>

        {/* Brass tripod lamp */}
        <g transform="translate(820 0)">
          {/* shade */}
          <path d="M 12 38 L 70 38 L 60 92 L 22 92 Z" fill="#e8d4a8" />
          <path d="M 12 38 L 70 38 L 60 92 L 22 92 Z" fill="url(#brass)" opacity="0.25" />
          {/* warm light glow */}
          <ellipse cx="41" cy="105" rx="40" ry="12" fill="#ffd9a0" opacity="0.18" />
          {/* tripod legs */}
          <line x1="41" y1="92" x2="14" y2="190" stroke="url(#brass)" strokeWidth="2.4" />
          <line x1="41" y1="92" x2="41" y2="190" stroke="url(#brass)" strokeWidth="2.4" />
          <line x1="41" y1="92" x2="68" y2="190" stroke="url(#brass)" strokeWidth="2.4" />
          <circle cx="41" cy="92" r="3" fill="#3a2a18" />
        </g>

        <ellipse cx="250" cy="200" rx="170" ry="7" fill="#000" opacity="0.22" />
        <ellipse cx="861" cy="200" rx="60"  ry="5" fill="#000" opacity="0.25" />
      </svg>
    );
  }

  // dark / modern
  return (
    <svg
      viewBox="0 0 1000 220"
      preserveAspectRatio="xMidYMax meet"
      className="absolute left-0 right-0 bottom-0 w-full h-[36%] pointer-events-none"
    >
      <defs>
        <linearGradient id="darkSofa" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3a3a3a" />
          <stop offset="100%" stopColor="#0d0d0d" />
        </linearGradient>
        <linearGradient id="darkSeat" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#2a2a2a" />
          <stop offset="100%" stopColor="#0a0a0a" />
        </linearGradient>
        <linearGradient id="monsteraLeaf" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#3a6a3f" />
          <stop offset="100%" stopColor="#1a3a22" />
        </linearGradient>
        <linearGradient id="pot" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3a3026" />
          <stop offset="100%" stopColor="#1a1409" />
        </linearGradient>
      </defs>

      {/* Modular dark sofa */}
      <g transform="translate(140 36)">
        {/* back */}
        <path d="M 0 60 Q 0 20 18 20 L 282 20 Q 300 20 300 60 L 300 92 L 0 92 Z" fill="url(#darkSofa)" />
        {/* cushions */}
        <rect x="6"   y="80" width="92"  height="48" rx="6" fill="url(#darkSeat)" />
        <rect x="104" y="80" width="92"  height="48" rx="6" fill="#1f1f1f" />
        <rect x="202" y="80" width="92"  height="48" rx="6" fill="url(#darkSeat)" />
        {/* base */}
        <rect x="-6" y="126" width="312" height="14" fill="#0a0a0a" />
        {/* legs */}
        <rect x="10"  y="140" width="6" height="14" fill="#000" />
        <rect x="284" y="140" width="6" height="14" fill="#000" />
        {/* highlight */}
        <ellipse cx="150" cy="46" rx="120" ry="6" fill="#ffffff" opacity="0.05" />
      </g>

      {/* Monstera plant in dark pot */}
      <g transform="translate(800 -10)">
        {/* leaves */}
        <g>
          <path d="M40 130 C 6 120 -6 70 22 40 C 38 56 46 90 40 130 Z" fill="url(#monsteraLeaf)" />
          <path d="M40 130 C 4 110 4 90 26 76" stroke="#1a3a22" strokeWidth="1" fill="none" />
          <path d="M48 110 C 84 96 92 56 60 30 C 50 56 46 86 48 110 Z" fill="url(#monsteraLeaf)" />
          <path d="M52 96 C 78 80 86 52 64 36" stroke="#1a3a22" strokeWidth="1" fill="none" />
          <path d="M30 120 C 0 130 -6 152 22 152 C 30 144 32 132 30 120 Z" fill="#274a2a" />
          <path d="M54 130 C 88 124 94 100 70 96 C 60 110 56 122 54 130 Z" fill="#274a2a" />
        </g>
        {/* stem */}
        <path d="M40 132 L 40 158" stroke="#1a3a22" strokeWidth="2.4" />
        {/* pot */}
        <path d="M 12 158 L 70 158 L 64 208 L 18 208 Z" fill="url(#pot)" />
        <ellipse cx="41" cy="158" rx="29" ry="4" fill="#0a0a0a" />
      </g>

      <ellipse cx="290" cy="200" rx="170" ry="6" fill="#000" opacity="0.4" />
      <ellipse cx="840" cy="206" rx="50"  ry="5" fill="#000" opacity="0.4" />
    </svg>
  );
};

const FRAMES = [
  // bw = frame border width (% of artwork width), mw = mat width (% of artwork width)
  { key: "standard", label: "Standard",  label_en: "Standard", border: "#1a1a1a", mat: "#f5f3ee", bw: 1.6, mw: 6.5 },
  { key: "noir",     label: "Noir",      label_en: "Black",    border: "#0a0a0a", mat: "#f5f3ee", bw: 2.4, mw: 5.5 },
  { key: "blanc",    label: "Blanc",     label_en: "White",    border: "#f0eee7", mat: "#0a0a0a", bw: 2.4, mw: 5.5 },
  { key: "chene",    label: "Chêne",     label_en: "Oak",      border: "#b08758", mat: "#f5f3ee", bw: 2.0, mw: 6.0 },
  { key: "noyer",    label: "Noyer",     label_en: "Walnut",   border: "#5a3a26", mat: "#f5f3ee", bw: 2.0, mw: 6.0 },
  { key: "or",       label: "Or",        label_en: "Gold",     border: "#b08b3b", mat: "#f5f3ee", bw: 1.8, mw: 5.5 },
  { key: "none",     label: "Sans cadre", label_en: "Unframed", border: "transparent", mat: "transparent", bw: 0, mw: 0 },
];

const sizeToWidthPct = (sizeKey) => {
  const map = { "20x30": 7, "30x45": 11, "40x60": 14, "50x70": 17, "60x90": 21, "80x120": 28 };
  return map[sizeKey] || 14;
};
const sizeAspect = (sizeKey) => {
  const [w, h] = sizeKey.split("x").map(Number);
  return h / w;
};

const SAFE_MARGIN = 4;

export const RoomVisualizer = ({ imageSrc, sizeKey, productTitle }) => {
  const t = useT();
  const lang = typeof document !== "undefined" ? document.documentElement.lang : "fr";
  const stageRef = useRef(null);
  const stageInnerRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragState = useRef(null);

  const [scene, setScene] = useState(ROOMS[0]);
  const [frame, setFrame] = useState(FRAMES[0]);
  const [pos, setPos] = useState({ x: 50, y: 32 });
  const [dragging, setDragging] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [stageWidth, setStageWidth] = useState(800);

  // Wall calibration state (for uploaded photos)
  const [wallCanvas, setWallCanvas] = useState(null); // HTMLCanvasElement of the uploaded image
  const [wallSamples, setWallSamples] = useState([]); // {r,g,b,u,v}
  const [calibrationMode, setCalibrationMode] = useState(false);

  // Share state
  const [shareOpen, setShareOpen] = useState(false);
  const [shareImg, setShareImg] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const widthPct = useMemo(() => sizeToWidthPct(sizeKey), [sizeKey]);
  const aspect = useMemo(() => sizeAspect(sizeKey), [sizeKey]);
  const heightPct = widthPct * aspect * (16 / 9);
  const halfW = widthPct / 2;
  const halfH = heightPct / 2;

  // Proportional frame/mat in pixels — they scale with artwork size & stage
  const artworkPxWidth = (stageWidth * widthPct) / 100;
  const framePx = frame.bw > 0 ? Math.max(2, Math.round(artworkPxWidth * (frame.bw / 100))) : 0;
  const matPx = frame.mw > 0 ? Math.max(0, Math.round(artworkPxWidth * (frame.mw / 100))) : 0;

  // Track stage width for proportional calculations
  useEffect(() => {
    if (!stageRef.current) return;
    const update = () => {
      if (stageRef.current) setStageWidth(stageRef.current.getBoundingClientRect().width);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(stageRef.current);
    return () => ro.disconnect();
  }, []);

  const isOutside =
    pos.x - halfW < SAFE_MARGIN ||
    pos.x + halfW > 100 - SAFE_MARGIN ||
    pos.y - halfH < SAFE_MARGIN ||
    pos.y + halfH > 100 - SAFE_MARGIN;

  const handlePointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    setHasInteracted(true);
    dragState.current = { startX: e.clientX, startY: e.clientY, startPos: pos };
  };
  const handlePointerMove = (e) => {
    if (!dragging || !stageRef.current) return;
    const rect = stageRef.current.getBoundingClientRect();
    const dx = ((e.clientX - dragState.current.startX) / rect.width) * 100 / zoom;
    const dy = ((e.clientY - dragState.current.startY) / rect.height) * 100 / zoom;
    let nx = dragState.current.startPos.x + dx;
    let ny = dragState.current.startPos.y + dy;
    nx = Math.max(halfW, Math.min(100 - halfW, nx));
    ny = Math.max(halfH, Math.min(100 - halfH, ny));
    setPos({ x: nx, y: ny });
  };
  const handlePointerUp = (e) => {
    setDragging(false);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
  };

  // Switch to a built-in scene — clear any uploaded wall calibration state
  const selectScene = (r) => {
    setScene(r);
    setWallCanvas(null);
    setWallSamples([]);
    setCalibrationMode(false);
  };

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const src = ev.target.result;
      setScene({ id: "custom", kind: "image", label: "Votre pièce", label_en: "Your room", src });
      setWallCanvas(null);
      setWallSamples([]);
      // Load the image into an offscreen canvas for color analysis
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const maxDim = 1200;
        const scale = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
        const c = document.createElement("canvas");
        c.width = Math.max(1, Math.round(img.naturalWidth * scale));
        c.height = Math.max(1, Math.round(img.naturalHeight * scale));
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        setWallCanvas(c);
        setCalibrationMode(true);
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
  };

  // Map stage UV (0..1) to source image pixel coordinates, accounting for object-cover crop
  const stageUVToImage = (u, v, canvas) => {
    const c = canvas;
    if (!c || !stageRef.current) return null;
    const r = stageRef.current.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    const stageRatio = r.width / r.height;
    const imgRatio = c.width / c.height;
    let imgX, imgY;
    if (imgRatio > stageRatio) {
      const visW = c.height * stageRatio;
      const offX = (c.width - visW) / 2;
      imgX = offX + u * visW;
      imgY = v * c.height;
    } else {
      const visH = c.width / stageRatio;
      const offY = (c.height - visH) / 2;
      imgX = u * c.width;
      imgY = offY + v * visH;
    }
    return { x: Math.round(imgX), y: Math.round(imgY) };
  };

  const samplePixelAvg = (canvas, x, y, radius = 4) => {
    if (!canvas) return null;
    const x0 = Math.max(0, x - radius);
    const y0 = Math.max(0, y - radius);
    const w = Math.min(radius * 2 + 1, canvas.width - x0);
    const h = Math.min(radius * 2 + 1, canvas.height - y0);
    if (w <= 0 || h <= 0) return null;
    try {
      const data = canvas.getContext("2d").getImageData(x0, y0, w, h).data;
      let r = 0, g = 0, b = 0, n = 0;
      for (let i = 0; i < data.length; i += 4) {
        r += data[i]; g += data[i + 1]; b += data[i + 2]; n++;
      }
      return { r: r / n, g: g / n, b: b / n };
    } catch {
      return null;
    }
  };

  const colorDistance = (a, b) => {
    const dr = a.r - b.r, dg = a.g - b.g, db = a.b - b.b;
    return Math.sqrt(dr * dr + dg * dg + db * db);
  };

  const handleCalibrateClick = (e) => {
    if (!calibrationMode || !wallCanvas || !stageRef.current) return;
    const r = stageRef.current.getBoundingClientRect();
    const u = (e.clientX - r.left) / r.width;
    const v = (e.clientY - r.top) / r.height;
    if (u < 0 || u > 1 || v < 0 || v > 1) return;
    const pt = stageUVToImage(u, v, wallCanvas);
    if (!pt) return;
    const color = samplePixelAvg(wallCanvas, pt.x, pt.y, 6);
    if (!color) return;
    setWallSamples((prev) => {
      const next = [...prev, { ...color, u, v }];
      return next.slice(-3);
    });
  };

  // Detect if current artwork placement is over wall or over furniture/window/plant.
  // Pure derivation from state — no refs accessed in the dep array.
  const placementOnFurniture = useMemo(() => {
    if (scene.kind !== "image" || !wallCanvas || wallSamples.length === 0 || stageWidth <= 0) return false;
    const TOLERANCE = 58;
    const COLS = 5, ROWS = 4;
    // Pre-compute object-cover crop mapping (stage to image)
    const stageH = stageWidth * 9 / 16;
    const stageRatio = stageWidth / stageH;
    const imgRatio = wallCanvas.width / wallCanvas.height;
    let visW, visH, offX, offY;
    if (imgRatio > stageRatio) {
      visW = wallCanvas.height * stageRatio;
      visH = wallCanvas.height;
      offX = (wallCanvas.width - visW) / 2;
      offY = 0;
    } else {
      visW = wallCanvas.width;
      visH = wallCanvas.width / stageRatio;
      offX = 0;
      offY = (wallCanvas.height - visH) / 2;
    }
    const ctx = wallCanvas.getContext("2d");
    let onWall = 0, total = 0;
    for (let gy = 0; gy < ROWS; gy++) {
      for (let gx = 0; gx < COLS; gx++) {
        const fx = (gx + 0.5) / COLS;
        const fy = (gy + 0.5) / ROWS;
        const sxPct = pos.x - halfW + fx * widthPct;
        const syPct = pos.y - halfH + fy * heightPct;
        if (sxPct < 0 || sxPct > 100 || syPct < 0 || syPct > 100) continue;
        const u = sxPct / 100, v = syPct / 100;
        const ix = Math.round(offX + u * visW);
        const iy = Math.round(offY + v * visH);
        const x0 = Math.max(0, ix - 2);
        const y0 = Math.max(0, iy - 2);
        const w = Math.min(5, wallCanvas.width - x0);
        const h = Math.min(5, wallCanvas.height - y0);
        if (w <= 0 || h <= 0) continue;
        let px;
        try {
          const data = ctx.getImageData(x0, y0, w, h).data;
          let rr = 0, gg = 0, bb = 0, nn = 0;
          for (let i = 0; i < data.length; i += 4) {
            rr += data[i]; gg += data[i + 1]; bb += data[i + 2]; nn++;
          }
          px = { r: rr / nn, g: gg / nn, b: bb / nn };
        } catch {
          continue;
        }
        total++;
        const matches = wallSamples.some((ws) => colorDistance(px, ws) < TOLERANCE);
        if (matches) onWall++;
      }
    }
    return total > 0 && onWall / total < 0.55;
  }, [pos.x, pos.y, scene.kind, wallCanvas, wallSamples, widthPct, heightPct, halfW, halfH, stageWidth]);

  // Floor/furniture warning for built-in styled scenes (decor at bottom of stage)
  const placementOnStyledFloor =
    scene.kind === "styled" && pos.y + halfH > 80;

  const placementWarning = isOutside
    ? "Trop près du bord"
    : placementOnFurniture
    ? "Sur un meuble · replacez sur le mur"
    : placementOnStyledFloor
    ? "Sur le mobilier · remontez sur le mur"
    : null;

  // --- Share / Capture ---
  const handleOpenShare = async () => {
    if (!stageInnerRef.current) return;
    setShareOpen(true);
    setShareImg(null);
    setShareLoading(true);
    setLinkCopied(false);
    try {
      const canvas = await html2canvas(stageInnerRef.current, {
        backgroundColor: "#0a0a0a",
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        ignoreElements: (el) => el.dataset?.captureIgnore === "true",
      });
      setShareImg(canvas.toDataURL("image/png"));
    } catch (err) {
      console.error("Capture failed", err);
      setShareImg("error");
    } finally {
      setShareLoading(false);
    }
  };

  const handleDownload = () => {
    if (!shareImg || shareImg === "error") return;
    const a = document.createElement("a");
    a.href = shareImg;
    a.download = `apercu-${(productTitle || "tirage").replace(/\s+/g, "-")}-${sizeKey}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleCopyLink = async () => {
    try {
      const params = new URLSearchParams({
        room: scene.id,
        frame: frame.key,
        size: sizeKey,
        x: pos.x.toFixed(1),
        y: pos.y.toFixed(1),
        z: zoom.toString(),
      });
      const url = `${window.location.origin}${window.location.pathname}?viz=${encodeURIComponent(params.toString())}`;
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2200);
    } catch {
      // ignore
    }
  };

  const handleWebShare = async () => {
    if (!shareImg || shareImg === "error" || !navigator.share) return;
    try {
      const blob = await (await fetch(shareImg)).blob();
      const file = new File([blob], "apercu.png", { type: "image/png" });
      const data = {
        files: [file],
        title: `Mon aperçu — ${productTitle || "Tirage Romain Bruneau"}`,
        text: `Voici comment ce tirage se présenterait chez moi. ✨`,
      };
      if (navigator.canShare && !navigator.canShare(data)) {
        await navigator.share({ title: data.title, text: data.text });
      } else {
        await navigator.share(data);
      }
    } catch {
      // ignore (user cancelled)
    }
  };

  useEffect(() => {
    const t = setTimeout(() => {
      setPos((p) => ({
        x: Math.max(halfW, Math.min(100 - halfW, p.x)),
        y: Math.max(halfH, Math.min(100 - halfH, p.y)),
      }));
    }, 0);
    return () => clearTimeout(t);
  }, [sizeKey, halfH, halfW]);

  return (
    <div data-testid="room-visualizer" className="bg-ink rounded-2xl overflow-hidden border border-stone/30 shadow-[0_0_0_1px_rgba(184,169,154,0.12),0_30px_60px_-20px_rgba(0,0,0,0.7)] ring-1 ring-stone/10">
      {/* Interactive header bar — signals this is a live preview */}
      <div className="flex items-center justify-between gap-3 px-4 md:px-5 py-2.5 bg-gradient-to-r from-stone/15 via-stone/5 to-transparent border-b border-stone/15">
        <div className="flex items-center gap-2.5">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
          </span>
          <span className="text-[10px] tracking-[0.28em] uppercase text-bone/90 font-medium">
            Aperçu interactif · En direct
          </span>
        </div>
        <span className="hidden sm:inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-stone">
          <MousePointer2 size={11} /> Cliquez · Glissez · Zoomez
        </span>
      </div>

      {/* Stage */}
      <div
        ref={stageRef}
        onClick={handleCalibrateClick}
        className={`relative w-full aspect-[16/9] overflow-hidden select-none bg-ink group/stage ${
          calibrationMode ? "cursor-crosshair" : ""
        }`}
        style={{ touchAction: "none" }}
      >
        {/* Zoomable scene wrapper — captured for share */}
        <div
          ref={stageInnerRef}
          className="absolute inset-0 origin-center transition-transform duration-300"
          style={{ transform: `scale(${zoom})` }}
        >
          {scene.kind === "styled" ? (
            <div className="absolute inset-0" style={scene.style}>
              <div className="absolute left-0 right-0 h-px bg-black/15" style={{ top: "84%" }} />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(ellipse at 50% 28%, rgba(255,240,210,0.20) 0%, rgba(0,0,0,0) 55%)",
                }}
              />
              {/* Decor furniture/plants */}
              {scene.decor && <SceneDecor kind={scene.decor} />}
            </div>
          ) : (
            <img
              src={scene.src}
              alt={scene.label || "Pièce"}
              className="absolute inset-0 w-full h-full object-cover"
              draggable={false}
            />
          )}

          {/* Draggable artwork */}
          <div
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onClick={(e) => e.stopPropagation()}
            data-testid="visualizer-artwork"
            className={`absolute -translate-x-1/2 -translate-y-1/2 ${
              dragging ? "cursor-grabbing" : "cursor-grab"
            } ${!hasInteracted ? "animate-pulse-soft" : ""}`}
            style={{
              left: `${pos.x}%`,
              top: `${pos.y}%`,
              width: `${widthPct}%`,
              padding: `${framePx}px`,
              background: frame.border,
              boxShadow: dragging
                ? "0 14px 32px rgba(0,0,0,0.55), 0 0 0 2px rgba(184,169,154,0.7)"
                : !hasInteracted
                ? "0 8px 20px rgba(0,0,0,0.45), 0 0 0 2px rgba(245,243,238,0.55), 0 0 24px 4px rgba(245,243,238,0.18)"
                : "0 8px 20px rgba(0,0,0,0.45), 0 1px 0 rgba(255,255,255,0.05) inset",
              outline: isOutside ? "2px solid rgba(220, 38, 38, 0.85)" : "none",
              outlineOffset: "3px",
              transition: dragging ? "none" : "outline 0.2s ease, box-shadow 0.3s ease",
            }}
          >
            {/* Mat */}
            <div
              style={{
                background: frame.mat,
                padding: `${matPx}px`,
                boxShadow: matPx > 0 ? "inset 0 0 0 1px rgba(0,0,0,0.06)" : "none",
              }}
            >
              {/* Artwork — div with background-image to enforce aspect ratio reliably */}
              <div
                className="w-full"
                style={{
                  aspectRatio: `1 / ${aspect}`,
                  backgroundImage: `url("${imageSrc}")`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundColor: "#0a0a0a",
                }}
                role="img"
                aria-label={productTitle}
              />
            </div>
          </div>

          {/* First-time interactive hint — disappears after first drag */}
          {!hasInteracted && (
            <div
              data-capture-ignore="true"
              className="absolute pointer-events-none flex items-center gap-2 px-3 py-2 rounded-full bg-ink/85 backdrop-blur-md border border-stone/40 text-bone text-[10px] tracking-[0.22em] uppercase shadow-2xl animate-bounce-slow"
              style={{
                left: `${pos.x}%`,
                top: `calc(${pos.y}% + ${(heightPct / 2) + 4}%)`,
                transform: "translate(-50%, 0)",
              }}
            >
              <Hand size={12} className="text-emerald-300" />
              <span>Glissez l&apos;œuvre</span>
            </div>
          )}
        </div>

        {/* Zoom controls */}
        <div className="absolute top-3 right-3 z-20 flex flex-col bg-ink/70 backdrop-blur-sm border border-bone/15 rounded">
          <button
            data-testid="viz-zoom-in"
            onClick={() => { setHasInteracted(true); setZoom((z) => Math.min(2.5, +(z + 0.25).toFixed(2))); }}
            className="p-2 text-bone hover:text-stone"
            aria-label="Zoom avant"
          >
            <ZoomIn size={14} />
          </button>
          <div className="h-px bg-bone/15" />
          <button
            data-testid="viz-zoom-out"
            onClick={() => { setHasInteracted(true); setZoom((z) => Math.max(1, +(z - 0.25).toFixed(2))); }}
            className="p-2 text-bone hover:text-stone"
            aria-label="Zoom arrière"
          >
            <ZoomOut size={14} />
          </button>
        </div>

        {/* Hint + warnings */}
        <div data-capture-ignore="true" className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-[10px] tracking-[0.2em] uppercase text-bone pointer-events-none gap-2">
          <span className="bg-ink/60 backdrop-blur-sm px-3 py-1.5 flex items-center">
            <Sparkles size={11} className="inline mr-1.5" />
            Glissez · {zoom > 1 ? `Zoom ${zoom}×` : "Zoom 1×"}
          </span>
          {placementWarning && (
            <span className="bg-red-600/85 text-white px-3 py-1.5 shadow-lg animate-pulse">
              {placementWarning}
            </span>
          )}
        </div>

        {/* Calibration overlay — visible while in calibration mode for uploaded photos */}
        {scene.kind === "image" && calibrationMode && (
          <div data-capture-ignore="true" className="absolute inset-0 z-30 pointer-events-none">
            {/* Subtle dim + crosshair guides */}
            <div className="absolute inset-0 bg-ink/25" />
            <div className="absolute top-3 left-3 right-3 flex flex-wrap items-center justify-between gap-2 pointer-events-auto">
              <div className="bg-ink/90 backdrop-blur-md border border-emerald-400/50 text-bone px-3.5 py-2 rounded-md flex items-center gap-2 shadow-2xl max-w-[80%]">
                <Pipette size={13} className="text-emerald-300 shrink-0" />
                <div className="text-[10px] tracking-[0.2em] uppercase leading-tight">
                  Cliquez sur le mur <span className="text-bone/60 normal-case tracking-normal">({wallSamples.length}/3)</span>
                  <div className="text-[9px] text-bone/60 normal-case tracking-normal mt-0.5">
                    Échantillonnez 1 à 3 zones (peinture, papier-peint…) pour détecter les meubles.
                  </div>
                </div>
              </div>
              <div className="flex gap-1.5">
                {wallSamples.length > 0 && (
                  <button
                    data-testid="viz-calibrate-reset"
                    onClick={(e) => { e.stopPropagation(); setWallSamples([]); }}
                    className="bg-ink/80 backdrop-blur-md border border-stone/40 text-bone hover:text-white text-[10px] tracking-[0.2em] uppercase px-3 py-2 rounded-md inline-flex items-center gap-1.5"
                  >
                    <RotateCcw size={11} /> Reset
                  </button>
                )}
                <button
                  data-testid="viz-calibrate-done"
                  onClick={(e) => { e.stopPropagation(); setCalibrationMode(false); }}
                  className="bg-emerald-500 text-ink hover:bg-emerald-400 text-[10px] tracking-[0.2em] uppercase px-3 py-2 rounded-md inline-flex items-center gap-1.5 font-medium shadow-lg"
                >
                  <Check size={11} /> Terminé
                </button>
              </div>
            </div>
            {/* Sample markers */}
            {wallSamples.map((s, i) => (
              <div
                key={i}
                className="absolute -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-6 h-6 rounded-full ring-2 ring-emerald-300 shadow-[0_0_0_2px_rgba(0,0,0,0.5)]"
                style={{
                  left: `${s.u * 100}%`,
                  top: `${s.v * 100}%`,
                  background: `rgb(${Math.round(s.r)},${Math.round(s.g)},${Math.round(s.b)})`,
                }}
              >
                <Check size={11} className="text-ink mix-blend-difference" />
              </div>
            ))}
          </div>
        )}

        {/* "Recalibrate" pill — shown after calibration is done on uploaded scenes */}
        {scene.kind === "image" && !calibrationMode && wallSamples.length > 0 && (
          <button
            data-testid="viz-recalibrate"
            onClick={(e) => { e.stopPropagation(); setCalibrationMode(true); }}
            data-capture-ignore="true"
            className="absolute top-3 left-3 z-20 bg-ink/70 backdrop-blur-sm border border-bone/15 text-bone hover:text-white text-[10px] tracking-[0.22em] uppercase px-3 py-1.5 rounded inline-flex items-center gap-1.5"
            title="Re-calibrer la détection du mur"
          >
            <Crosshair size={11} className="text-emerald-300" />
            Re-calibrer
            <span className="ml-1 flex gap-0.5">
              {wallSamples.map((s, i) => (
                <span key={i} className="w-2 h-2 rounded-full ring-1 ring-bone/40"
                  style={{ background: `rgb(${Math.round(s.r)},${Math.round(s.g)},${Math.round(s.b)})` }} />
              ))}
            </span>
          </button>
        )}
      </div>

      {/* Controls */}
      <div className="p-5 md:p-6 space-y-5 bg-ink">
        {/* Rooms */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="eyebrow text-stone">{t("room.eyebrow")} · {pickLabel(scene, lang)}</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-bone/45">
              {ROOMS.length} ambiances
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {ROOMS.map((r) => {
              const isActive = scene.id === r.id;
              return (
                <button
                  key={r.id}
                  data-testid={`viz-room-${r.id}`}
                  onClick={() => selectScene(r)}
                  title={r.label}
                  className={`group flex-shrink-0 w-20 h-14 rounded-md overflow-hidden border-2 transition-all relative ${
                    isActive ? "border-stone" : "border-transparent hover:border-stone/40"
                  }`}
                >
                  {r.kind === "styled" ? (
                    <div className="w-full h-full" style={r.style} />
                  ) : (
                    <img src={r.src} alt={r.label} className="w-full h-full object-cover" loading="lazy" />
                  )}
                </button>
              );
            })}
            <button
              data-testid="viz-upload"
              onClick={() => fileInputRef.current?.click()}
              className={`flex-shrink-0 w-20 h-14 rounded-md border-2 border-dashed flex flex-col items-center justify-center transition-colors ${
                scene.id === "custom"
                  ? "border-stone text-stone"
                  : "border-stone/30 text-bone/55 hover:border-stone/60"
              }`}
            >
              <Upload size={14} />
              <span className="text-[9px] tracking-[0.18em] uppercase mt-0.5">Upload</span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleUpload}
              className="hidden"
            />
          </div>
        </div>

        {/* Frames */}
        <div>
          <div className="eyebrow text-stone mb-3">Cadre</div>
          <div className="flex flex-wrap gap-2">
            {FRAMES.map((f) => (
              <button
                key={f.key}
                data-testid={`viz-frame-${f.key}`}
                onClick={() => setFrame(f)}
                className={`flex items-center gap-2 px-3 py-2 border text-[10px] tracking-[0.18em] uppercase transition-colors ${
                  frame.key === f.key
                    ? "border-stone text-bone"
                    : "border-stone/25 text-bone/55 hover:border-stone/55"
                }`}
              >
                <span
                  className="w-4 h-4 rounded-sm border border-bone/20"
                  style={{
                    background: f.key === "none" ? "transparent" : f.border,
                    backgroundImage:
                      f.key === "none"
                        ? "linear-gradient(45deg, rgba(245,243,238,0.2) 25%, transparent 25%, transparent 75%, rgba(245,243,238,0.2) 75%, rgba(245,243,238,0.2))"
                        : undefined,
                    backgroundSize: f.key === "none" ? "6px 6px" : undefined,
                  }}
                />
                {pickLabel(f, lang)}
              </button>
            ))}
          </div>
        </div>

        {/* Actions row */}
        <div className="flex items-center justify-between pt-2 border-t border-stone/15 gap-3 flex-wrap">
          <div className="text-[10px] tracking-[0.2em] uppercase text-bone/55">
            Format · {sizeKey.replace("x", " × ")} cm
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="viz-reset"
              onClick={() => {
                setPos({ x: 50, y: 32 });
                setZoom(1);
              }}
              className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-stone hover:text-bone transition-colors"
            >
              <RotateCcw size={12} /> Recentrer
            </button>
            <button
              data-testid="viz-share"
              onClick={handleOpenShare}
              className="relative flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase bg-emerald-500 text-ink hover:bg-emerald-400 px-4 py-2 rounded-full font-medium shadow-[0_8px_24px_-8px_rgba(16,185,129,0.6)] transition-all hover:scale-[1.03]"
            >
              <Share2 size={12} /> Partager mon aperçu
            </button>
          </div>
        </div>
      </div>

      {/* Share modal */}
      {shareOpen && (
        <div
          data-testid="share-modal"
          className="fixed inset-0 z-[100] bg-ink/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
          onClick={() => setShareOpen(false)}
        >
          <div
            className="bg-ink text-bone w-full max-w-2xl rounded-2xl overflow-hidden border border-stone/25 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-stone/15">
              <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase">
                <Share2 size={12} className="text-emerald-300" />
                Partager mon aperçu
              </div>
              <button
                data-testid="share-close"
                onClick={() => setShareOpen(false)}
                className="text-bone/70 hover:text-bone p-1"
                aria-label="Fermer"
              >
                <XIcon size={18} />
              </button>
            </div>
            <div className="p-5 md:p-6 space-y-5">
              {/* Preview */}
              <div className="aspect-[16/9] bg-ink/60 rounded-md overflow-hidden border border-stone/20 flex items-center justify-center">
                {shareLoading && (
                  <div className="text-[10px] tracking-[0.22em] uppercase text-bone/60 animate-pulse">
                    Génération de l&apos;aperçu…
                  </div>
                )}
                {!shareLoading && shareImg && shareImg !== "error" && (
                  <img
                    src={shareImg}
                    alt="Aperçu à partager"
                    className="w-full h-full object-contain"
                    data-testid="share-preview"
                  />
                )}
                {!shareLoading && shareImg === "error" && (
                  <div className="text-[10px] tracking-[0.22em] uppercase text-red-400">
                    Impossible de générer l&apos;aperçu
                  </div>
                )}
              </div>

              {/* Metadata */}
              <div className="text-[10px] tracking-[0.22em] uppercase text-bone/55 flex flex-wrap gap-x-4 gap-y-1">
                <span>{productTitle || "Tirage"}</span>
                <span>·</span>
                <span>{sizeKey.replace("x", " × ")} cm</span>
                <span>·</span>
                <span>Cadre {frame.label}</span>
                <span>·</span>
                <span>{t("room.eyebrow")} · {pickLabel(scene, lang)}</span>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <button
                  data-testid="share-download"
                  onClick={handleDownload}
                  disabled={!shareImg || shareImg === "error"}
                  className="flex-1 min-w-[160px] flex items-center justify-center gap-2 text-[10px] tracking-[0.22em] uppercase bg-bone text-ink px-4 py-3 rounded-full font-medium hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download size={13} /> Télécharger
                </button>
                <button
                  data-testid="share-copy-link"
                  onClick={handleCopyLink}
                  className="flex-1 min-w-[160px] flex items-center justify-center gap-2 text-[10px] tracking-[0.22em] uppercase border border-stone/40 text-bone hover:bg-stone/10 px-4 py-3 rounded-full transition-colors"
                >
                  {linkCopied ? (
                    <>
                      <Check size={13} className="text-emerald-300" /> Lien copié
                    </>
                  ) : (
                    <>
                      <Share2 size={13} /> Copier le lien
                    </>
                  )}
                </button>
                {typeof navigator !== "undefined" && navigator.share && (
                  <button
                    data-testid="share-native"
                    onClick={handleWebShare}
                    disabled={!shareImg || shareImg === "error"}
                    className="flex-1 min-w-[160px] flex items-center justify-center gap-2 text-[10px] tracking-[0.22em] uppercase bg-emerald-500 text-ink hover:bg-emerald-400 px-4 py-3 rounded-full font-medium transition-colors disabled:opacity-50"
                  >
                    <Share2 size={13} /> Envoyer…
                  </button>
                )}
              </div>

              <p className="text-[10px] tracking-[0.06em] text-bone/45 leading-relaxed">
                Astuce : envoyez l&apos;aperçu à un proche pour avoir son avis, ou enregistrez-le pour le retrouver plus tard. Le lien renvoie vers la boutique de Romain.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomVisualizer;
